import type { GridApi } from 'ag-grid-community';
import _ from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { AgGridBaseUrlSyncConfig, GridUrlState, UrlParams } from './AgGridBase.config';
import { DEFAULT_URL_SYNC_DEBOUNCE_MS } from './AgGridBase.config';

type GridRef = React.RefObject<{ api?: GridApi | null } | null>;

function readSortModelFromApi(api: GridApi): GridUrlState['sortModel'] {
  const columnState = api.getColumnState() ?? [];
  return columnState
    .filter((col) => typeof col.sort === 'string' && col.sort)
    .map((col) => ({ colId: col.colId as string, sort: col.sort as 'asc' | 'desc' }));
}

function applySortToApi(api: GridApi, sortModel: GridUrlState['sortModel']) {
  const columnState = api.getColumnState() ?? [];
  if (sortModel.length > 0) {
    const targetSort = sortModel[0];
    const newState = columnState.map((col) =>
      col.colId === targetSort.colId ? { ...col, sort: targetSort.sort } : { ...col, sort: null }
    );
    api.applyColumnState({ state: newState, applyOrder: true });
  } else {
    const newState = columnState.map((col) => ({ ...col, sort: null }));
    api.applyColumnState({ state: newState, applyOrder: true });
  }
}

function isUrlSyncActive(config: AgGridBaseUrlSyncConfig | undefined): config is AgGridBaseUrlSyncConfig & {
  adapter: NonNullable<AgGridBaseUrlSyncConfig['adapter']>;
  getUrlState: NonNullable<AgGridBaseUrlSyncConfig['getUrlState']>;
  setUrlState: NonNullable<AgGridBaseUrlSyncConfig['setUrlState']>;
} {
  return Boolean(
    config?.enabled &&
      config.adapter &&
      typeof config.getUrlState === 'function' &&
      typeof config.setUrlState === 'function'
  );
}

/**
 * URL filter/sort hydration and debounced outbound sync with loop prevention and adapter error fallback.
 */
export function useAgGridUrlSync(gridRef: GridRef, urlSync: AgGridBaseUrlSyncConfig | undefined, gridReadySignal: number) {
  const isApplyingUrlStateRef = useRef(false);
  const initialHydrationDoneRef = useRef(false);
  const lastSyncedRevisionRef = useRef<unknown>(undefined);
  const currentQueryRef = useRef<string | undefined>(undefined);

  const getApi = useCallback(() => gridRef.current?.api ?? null, [gridRef]);

  const safeFromUrl = useCallback((): GridUrlState => {
    if (!isUrlSyncActive(urlSync)) {
      return { filterModel: {}, sortModel: [], q: undefined };
    }
    try {
      return urlSync.adapter.fromUrl(urlSync.getUrlState() ?? {});
    } catch {
      return { filterModel: {}, sortModel: [], q: undefined };
    }
  }, [urlSync]);

  const applyUrlToGrid = useCallback(
    (state: GridUrlState) => {
      const api = getApi();
      if (!api) {
        return;
      }
      isApplyingUrlStateRef.current = true;
      try {
        // Set the current filter model (specific fields only)
        const currentFilter = api.getFilterModel() ?? {};
        const previousQuery = currentQueryRef.current;
        const nextQuery = _.isEmpty(state.q) ? undefined : state.q;
        const queryChanged = previousQuery !== nextQuery;
        currentQueryRef.current = nextQuery;
        if (!_.isEqual(currentFilter, state.filterModel)) {
          api.setFilterModel(state.filterModel as Record<string, unknown>);
        }
        // Set the current sort model
        const currentSort = readSortModelFromApi(api);
        if (!_.isEqual(currentSort, state.sortModel)) {
          applySortToApi(api, state.sortModel);
        }
        if (queryChanged) {
          api.refreshServerSide({ purge: true });
        }
      } finally {
        window.setTimeout(() => {
          isApplyingUrlStateRef.current = false;
        }, 0);
      }
    },
    [getApi]
  );

  const pushUrlFromGrid = useCallback(() => {
    if (!isUrlSyncActive(urlSync) || isApplyingUrlStateRef.current) {
      return;
    }
    if (!initialHydrationDoneRef.current) {
      return;
    }
    const api = getApi();
    if (!api) {
      return;
    }
    const filterModel = api.getFilterModel() ?? {};
    const sortModel = readSortModelFromApi(api);
    const q = urlSync.getUrlState()?.q as string | undefined;
    const gridParams = urlSync.adapter.toUrl({
      filterModel,
      sortModel,
      q,
    }) as UrlParams;
    const preserved =
      urlSync.preserveKeys && urlSync.preserveKeys.length > 0
        ? _.pick(urlSync.getUrlState(), urlSync.preserveKeys)
        : {};
    const merged: UrlParams = { ...preserved, ...gridParams };
    urlSync.setUrlState(merged, { replace: urlSync.replaceHistory !== false });
  }, [getApi, urlSync]);

  const debouncedPush = useMemo(() => {
    const ms = urlSync?.debounceMs ?? DEFAULT_URL_SYNC_DEBOUNCE_MS;
    return _.debounce(pushUrlFromGrid, ms);
  }, [pushUrlFromGrid, urlSync?.debounceMs]);

  useEffect(() => () => debouncedPush.cancel(), [debouncedPush]);

  const onFilterOrSortChanged = useCallback(() => {
    if (!isUrlSyncActive(urlSync)) {
      return;
    }
    if (isApplyingUrlStateRef.current) {
      return;
    }
    if (!initialHydrationDoneRef.current) {
      return;
    }
    debouncedPush();
  }, [debouncedPush, urlSync]);

  useEffect(() => {
    if (!isUrlSyncActive(urlSync)) {
      return;
    }
    if (urlSync.isUrlLoading) {
      return;
    }
    const api = getApi();
    if (!api) {
      return;
    }
    if (gridReadySignal === 0) {
      return;
    }

    if (!initialHydrationDoneRef.current) {
      applyUrlToGrid(safeFromUrl());
      initialHydrationDoneRef.current = true;
      lastSyncedRevisionRef.current = urlSync.urlStateRevision;
      return;
    }

    if (isApplyingUrlStateRef.current) {
      return;
    }

    if (_.isEqual(lastSyncedRevisionRef.current, urlSync.urlStateRevision)) {
      return;
    }

    lastSyncedRevisionRef.current = urlSync.urlStateRevision;

    const next = safeFromUrl();
    const nextQuery = _.isEmpty(next.q) ? undefined : next.q;

    const currentFilter = api.getFilterModel() ?? {};
    const currentSort = readSortModelFromApi(api);
    if (
      _.isEqual(currentFilter, next.filterModel) &&
      _.isEqual(currentSort, next.sortModel) &&
      currentQueryRef.current === nextQuery
    ) {
      return;
    }
    applyUrlToGrid(next);
  }, [
    applyUrlToGrid,
    getApi,
    gridReadySignal,
    safeFromUrl,
    urlSync?.enabled,
    urlSync?.isUrlLoading,
    urlSync?.urlStateRevision,
  ]);

  return {
    onFilterOrSortChanged,
    /** Flush pending debounced URL write (e.g. before unmount) */
    flushUrlSync: () => debouncedPush.flush(),
  };
}
